<template>
  <ActivityFrame :hero="model.hero">
    <div class="activity-vote">
      <section class="activity-section vote-section">
        <h2 class="vote-title">投票应援</h2>
        <div ref="stageRail" class="vote-stages" :class="{ 'is-dragging': stageDragging }" role="group" aria-label="投票阶段" data-page-swipe-ignore
          @pointerdown="startStageDrag" @pointermove="moveStageDrag" @pointerup="endStageDrag" @pointercancel="endStageDrag" @lostpointercapture="endStageDrag"
          @click.capture="guardStageClick" @wheel="wheelStages" @keydown="keyStages" @scroll="rememberStageScroll">
          <div v-for="stage in stages" :key="stage.id" class="vote-stage">
            <span class="vote-stage-status">{{ stageStatus(stage.status) }}</span>
            <ActivityAction variant="secondary" :pressed="stage.id === model.stageId" :disabled="Boolean(busy) || model.publicReadOnly"
              :aria-label="`${stage.label}，${stageStatus(stage.status)}`" @click="selectStage(stage.id)">
              {{ stage.label }}
            </ActivityAction>
          </div>
        </div>
        <div class="vote-balance">
          <span>本阶段剩余票数</span>
          <strong>{{ model.availableVotes }}</strong><span>票</span>
        </div>

        <p v-if="currentStage?.publicDetail" class="vote-period">{{ currentStage.publicDetail }}</p>
        <p v-else-if="currentStage?.startAt && currentStage?.endAt" class="vote-period">{{ currentStage.startAt }} 至 {{ currentStage.endAt }}（北京时间）</p>
        <div v-if="podium.length" class="vote-podium" :class="`vote-podium--${podium.length}`" role="list" :aria-label="model.rankIsDisplayOrder ? '按票数排列的前三项' : '前三名'" :aria-description="model.rankingNotice" :title="model.rankingNotice">
          <div v-for="candidate in podium" :key="candidate.id" class="vote-podium-entry" role="listitem">
            <div class="vote-avatar vote-avatar--podium">
              <div class="vote-logo vote-logo--podium" aria-hidden="true">
                <img v-if="hasLogo(candidate)" :src="candidate.logo" alt=""
                  @error="failedLogos.add(candidate.logo)" />
                <span v-else>{{ abbreviation(candidate.name) }}</span>
              </div>
              <span class="vote-rank">{{ rankLabel(candidate) }}</span>
              <div class="vote-rank-badge" aria-hidden="true">
                <img v-if="!failedRankAssets.has(rankAsset(candidate.rank))" class="vote-rank-art"
                  :src="rankAsset(candidate.rank)" alt=""
                  @error="failedRankAssets.add(rankAsset(candidate.rank))" />
                <span v-if="Number(candidate.rank) > 3 || failedRankAssets.has(rankAsset(candidate.rank))" class="vote-rank-number">{{ candidate.rank }}</span>
              </div>
            </div>
            <span class="vote-name">{{ candidate.name }}</span>
            <span class="vote-count"><span class="vote-count-value">{{ candidate.votes ?? '—' }}</span><span class="vote-count-unit">票</span></span>
            <ActivityAction class="vote-button" variant="secondary" :disabled="!canVote" :loading="busy === `vote:${candidate.id}`"
              :aria-label="`为${candidate.name}投票`" @click="vote(candidate.id)">投票</ActivityAction>
          </div>
        </div>

        <div v-if="visibleOthers.length" class="vote-list" role="list" :aria-label="model.rankIsDisplayOrder ? '其余候选展示排序' : '其余战队排名'" :aria-description="model.rankingNotice" :title="model.rankingNotice">
          <div v-for="candidate in visibleOthers" :key="candidate.id" class="vote-row" role="listitem">
            <div class="vote-avatar">
              <div class="vote-logo" aria-hidden="true">
                <img v-if="hasLogo(candidate)" :src="candidate.logo" alt=""
                  @error="failedLogos.add(candidate.logo)" />
                <span v-else>{{ abbreviation(candidate.name) }}</span>
              </div>
              <div class="vote-row-rank vote-rank-badge" role="img" :aria-label="rankLabel(candidate)">
                <img v-if="!failedRankAssets.has(rankAsset(candidate.rank))" class="vote-rank-art"
                  :src="rankAsset(candidate.rank)" alt="" aria-hidden="true"
                  @error="failedRankAssets.add(rankAsset(candidate.rank))" />
                <span v-if="!knownRank(candidate.rank) || Number(candidate.rank) > 3 || failedRankAssets.has(rankAsset(candidate.rank))" class="vote-rank-number" aria-hidden="true">{{ candidate.rank ?? '—' }}</span>
              </div>
            </div>
            <div class="vote-row-copy">
              <span class="vote-name">{{ candidate.name }}</span>
              <span class="vote-count"><span class="vote-count-value">{{ candidate.votes ?? '—' }}</span><span class="vote-count-unit">票</span></span>
            </div>
            <ActivityAction class="vote-button vote-row-action" variant="secondary" :disabled="!canVote"
              :loading="busy === `vote:${candidate.id}`" :aria-label="`为${candidate.name}投票`"
              @click="vote(candidate.id)">投票</ActivityAction>
          </div>
        </div>
        <ActivityAction v-if="others.length > 3" variant="text" class="vote-more"
          :aria-expanded="expanded" @click="toggleExpanded">{{ expanded ? '收起' : '查看更多' }}</ActivityAction>
        <div class="activity-inline vote-links" role="group" aria-label="投票说明与记录">
          <ActivityAction variant="text" data-vote-info="rules" @click="emit('open-info', 'rules')">投票规则</ActivityAction>
          <ActivityAction variant="text" data-vote-info="records" @click="emit('open-info', 'records')">投票记录</ActivityAction>
        </div>
      </section>
    </div>
  </ActivityFrame>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import ActivityFrame from './ActivityFrame.vue';
import ActivityAction from './ActivityAction.vue';

const props = defineProps({
  model: { type: Object, required: true },
  busy: { type: String, default: '' },
  expanded: { type: Boolean, default: false },
  stageScrollLeft: { type: Number, default: 0 }
});
const emit = defineEmits(['update-stage', 'vote', 'open-info', 'update:expanded', 'update:stageScrollLeft']);
const stageRail = ref(null), stageDragging = ref(false);
let stagePointer = null, ignoreDragClick = false, railWindow = null, stageRailWidth = 0;
const railElement = () => stageRail.value?.$el || stageRail.value;
function setStageLeft(value) {
  const rail = railElement();
  if (rail) rail.scrollLeft = Math.max(0, Math.min(Math.max(0, rail.scrollWidth - rail.clientWidth), Number.isFinite(value) ? value : 0));
}
function rememberStageScroll() { const rail = railElement(); if (rail?.clientWidth) emit('update:stageScrollLeft', rail.scrollLeft); }
function getStageScrollLeft() { const rail = railElement(); return rail?.clientWidth ? rail.scrollLeft : props.stageScrollLeft; }
function restoreStageScroll(value) { stageRailWidth = railElement()?.clientWidth || 0; if (value === undefined) revealSelectedStage(); else setStageLeft(value); }
defineExpose({ getStageScrollLeft, restoreStageScroll });
function revealStage(button) {
  const rail = railElement();
  if (!rail || !button) return;
  const outer = rail.getBoundingClientRect(), inner = button.getBoundingClientRect();
  if (inner.left < outer.left) setStageLeft(rail.scrollLeft + inner.left - outer.left);
  else if (inner.right > outer.right) setStageLeft(rail.scrollLeft + inner.right - outer.right);
}
function revealSelectedStage() { revealStage(railElement()?.querySelector?.('[aria-pressed="true"]')); }
function resizeStageRail() {
  const width = railElement()?.clientWidth || 0;
  // Keyboard/browser chrome may change height; that must not reset a horizontal visit.
  if (width && width !== stageRailWidth) { stageRailWidth = width; revealSelectedStage(); }
}
function endStageDrag(event) {
  if (event && stagePointer?.id !== event.pointerId) return;
  const previous = stagePointer;
  stagePointer = null; stageDragging.value = false;
  if (previous?.element.hasPointerCapture?.(previous.id)) previous.element.releasePointerCapture(previous.id);
}
function startStageDrag(event) {
  if (event.isPrimary === false || event.button !== 0) return;
  endStageDrag(); ignoreDragClick = false;
  // Touch keeps the browser's native direction lock and momentum.
  if (event.pointerType === 'touch') return;
  const element = railElement();
  if (!element || element.scrollWidth <= element.clientWidth) return;
  stagePointer = { id: event.pointerId, x: event.clientX, y: event.clientY, left: element.scrollLeft, element };
}
function moveStageDrag(event) {
  if (!stagePointer || stagePointer.id !== event.pointerId) return;
  const dx = event.clientX - stagePointer.x, dy = event.clientY - stagePointer.y;
  if (!stageDragging.value) {
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
    if (Math.abs(dx) <= Math.abs(dy) * 1.15) { endStageDrag(); return; }
    stageDragging.value = true; ignoreDragClick = true;
    stagePointer.element.setPointerCapture?.(event.pointerId);
  }
  if (event.cancelable) event.preventDefault();
  setStageLeft(stagePointer.left - dx);
}
function guardStageClick(event) {
  // Only the click produced by this drag is swallowed; a new press resets it.
  if (ignoreDragClick && event.detail !== 0) {
    ignoreDragClick = false; event.preventDefault(); event.stopPropagation();
  }
}
function wheelStages(event) {
  const rail = railElement();
  if (!rail || !event.shiftKey || event.ctrlKey || event.metaKey || event.altKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY) || rail.scrollWidth <= rail.clientWidth) return;
  if (event.cancelable) event.preventDefault();
  const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rail.clientWidth : 1;
  setStageLeft(rail.scrollLeft + event.deltaY * scale);
}
function keyStages(event) {
  if (event.altKey || event.ctrlKey || event.metaKey || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  const buttons = [...(railElement()?.querySelectorAll('.activity-action:not([disabled])') || [])];
  const index = buttons.indexOf(event.target.closest?.('.activity-action'));
  if (index < 0) return;
  const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : Math.max(0, Math.min(buttons.length - 1, index + (event.key === 'ArrowRight' ? 1 : -1)));
  event.preventDefault(); buttons[next].focus({ preventScroll: true }); revealStage(buttons[next]);
}
watch(() => props.stageScrollLeft, value => setStageLeft(value), { flush: 'post' });
onMounted(() => {
  stageRailWidth = railElement()?.clientWidth || 0;
  if (props.stageScrollLeft > 0) setStageLeft(props.stageScrollLeft); else revealSelectedStage();
  railWindow = railElement()?.ownerDocument?.defaultView;
  railWindow?.addEventListener('resize', resizeStageRail);
});
onBeforeUnmount(() => { endStageDrag(); railWindow?.removeEventListener('resize', resizeStageRail); });
const expanded = ref(props.expanded);
watch(() => props.expanded, value => { expanded.value = value; });
function toggleExpanded() { expanded.value = !expanded.value; emit('update:expanded', expanded.value); }
let voteIntentPending = false;
const failedLogos = ref(new Set());
const failedRankAssets = ref(new Set());
const stages = computed(() => props.model.stages || []);
const currentStage = computed(() => stages.value.find(stage => stage.id === props.model.stageId));
// Sort a copy: supplied ranks remain authoritative and parent data stays untouched.
const candidates = computed(() => [...(props.model.candidates || [])].sort((a, b) => Number(a.rank) - Number(b.rank)));
const podium = computed(() => props.model.rankIsDisplayOrder && candidates.value.some(candidate => !knownRank(candidate.rank)) ? [] : candidates.value.slice(0, 3));
const others = computed(() => candidates.value.slice(podium.value.length));
const visibleOthers = computed(() => expanded.value ? others.value : others.value.slice(0, 3));
const canVote = computed(() => {
  const model = props.model;
  const knownVotes = typeof model.availableVotes === 'number' ||
    (typeof model.availableVotes === 'string' && model.availableVotes.trim() !== '');
  const votes = knownVotes ? Number(model.availableVotes) : NaN;
  // Legacy models use the stage and balance; explicit host qualification always wins.
  const qualified = (model.canVote === undefined || model.canVote === true) &&
    (model.eligible === undefined || model.eligible === true) &&
    (model.eligibility === undefined || model.eligibility?.canParticipate === true);
  return qualified && currentStage.value?.status === 'active' && knownVotes && Number.isSafeInteger(votes) && votes >= 1 && !props.busy;
});

// Collapse on the authoritative stage change, not on same-stage data refreshes.
watch(() => props.model.stageId, () => { if(expanded.value){expanded.value = false;emit('update:expanded',false);} }, { flush: 'sync' });
watch(() => props.model.stageId, revealSelectedStage, { flush: 'post' });
function stageStatus(status) {
  return { active: '进行中', upcoming: '未开始', finished: '已结束' }[status] || '';
}
function knownRank(value) { return value != null && value !== '' && Number.isSafeInteger(Number(value)) && Number(value) > 0; }
function rankLabel(candidate) {
  const rank = knownRank(candidate.rank) ? candidate.rank : '—';
  return props.model.rankIsDisplayOrder ? `展示序号 ${rank}` : `第 ${rank} 名`;
}
function selectStage(id) {
  if (!props.model.publicReadOnly && !props.busy && id !== props.model.stageId && stages.value.some(stage => stage.id === id)) emit('update-stage', id);
}
function vote(id) {
  if (!canVote.value || voteIntentPending || !candidates.value.some(candidate => candidate.id === id)) return;
  // Cover repeated DOM activation before the host's busy prop reaches this render.
  voteIntentPending = true;
  emit('vote', id);
  nextTick(() => { voteIntentPending = false; });
}
function hasLogo(candidate) {
  return Boolean(candidate.logo) && !failedLogos.value.has(candidate.logo);
}
function rankAsset(rank) {
  const value = Number(rank);
  return `/activity/h5/activity-demo-vote-rank-${[1, 2, 3].includes(value) ? value : 'other'}.png`;
}
function abbreviation(name) {
  return Array.from(String(name || '').trim()).slice(0, 4).join('');
}
</script>

<style scoped>
.activity-vote { --vote-ink: #0878cc; --vote-fill: #0878cc; min-width: 0; padding: 16px 16px 24px; background: var(--act-page, #f8f8f8); color: var(--act-text, #303442); font-family: var(--kg-font-ui, "PingFang SC"); font-size: 14px; line-height: 1.6; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
:global(html[data-theme='dark'] .activity-vote) { --vote-ink: #78c3ff; --act-disabled: #343946; }
.activity-vote .activity-section.vote-section { box-sizing: border-box; margin: 0; padding: 20px 16px 12px; border: 1px solid var(--act-line, #f2f2f6); border-radius: 16px; background: var(--act-surface, #fff); }
.activity-vote .vote-title { margin: 0 0 18px; font-family: inherit; font-size: 18px; font-weight: 600; line-height: 1.5; }
.vote-stages { display: flex; align-items: flex-end; gap: 10px; overflow-x: auto; overscroll-behavior-x: contain; -webkit-overflow-scrolling: touch; user-select: none; padding: 0 2px 16px; border-bottom: 1px solid var(--act-line, #f2f2f6); scrollbar-width: none; cursor: grab; }
.vote-stages.is-dragging, .vote-stages.is-dragging :deep(.activity-action) { cursor: grabbing; }
.vote-stages::-webkit-scrollbar { display: none; }
.vote-stage { flex: 0 0 auto; min-width: 112px; text-align: left; }
.vote-stage-status { display: block; margin: 0 0 6px 2px; color: var(--act-secondary, #616783); font-size: 11px; line-height: 1.5; }
.activity-vote .vote-stage :deep(.activity-action) { box-sizing: border-box; width: 100%; min-height: 44px; margin: 0; padding: 10px 16px; border: 1px solid var(--act-line, #e4e6ed); border-radius: 10px; background: var(--act-page, #f8f8f8); color: var(--act-secondary, #616783); font-size: 14px; font-weight: 500; }
.activity-vote .vote-stage :deep(.activity-action[aria-pressed='true']:not([disabled])), .activity-vote .vote-stage :deep(.activity-action[aria-pressed='true'][disabled]) { border-color: var(--vote-ink); background: var(--act-soft, #e8f5ff); color: var(--vote-ink); }
.vote-stage :deep(.activity-action__label) { white-space: nowrap; overflow-wrap: normal; }
.vote-balance { display: flex; align-items: baseline; gap: 6px; margin: 18px 0 0; color: var(--act-secondary, #616783); font-size: 13px; line-height: 1.5; }
.vote-balance strong { margin-left: auto; color: var(--vote-ink); font-family: var(--kg-font-digits, inherit); font-size: 30px; font-weight: 600; line-height: 1.2; }
.vote-period { margin: 10px 0 0; color: var(--act-secondary, #616783); font-size: 12px; line-height: 1.8; overflow-wrap: anywhere; }
.vote-podium { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin: 20px 0 0; }
.vote-podium-entry { box-sizing: border-box; display: flex; min-width: 0; flex-direction: column; align-items: center; padding: 16px 8px 12px; border: 1px solid var(--act-line, #f2f2f6); border-radius: 12px; background: var(--act-page, #f8f8f8); text-align: center; }
.vote-podium-entry:nth-child(1) { grid-column: 2; grid-row: 1; }
.vote-podium-entry:nth-child(2) { grid-column: 1; grid-row: 1; }
.vote-podium-entry:nth-child(3) { grid-column: 3; grid-row: 1; }
.vote-avatar { position: relative; flex: none; width: 52px; height: 52px; }
.vote-avatar--podium { width: 56px; height: 56px; margin: 0 0 12px; }
.vote-logo { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; border-radius: 10px; background: var(--act-surface, #fff); color: var(--act-secondary, #616783); text-align: center; line-height: 1.3; }
.vote-logo img { display: block; width: 100%; height: 100%; border-radius: inherit; }
.vote-logo span { max-width: 100%; overflow-wrap: anywhere; font-size: 12px; }
.vote-rank { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
.vote-rank-badge { box-sizing: border-box; position: absolute; right: -4px; bottom: -4px; display: flex; align-items: center; justify-content: center; width: 22px; min-height: 22px; color: #fff; font-size: 13px; line-height: 1.2; }
.vote-rank-art { position: absolute; inset: 0; display: block; width: 100%; height: 100%; }
.vote-rank-number { position: relative; max-width: 100%; padding: 0 1px; font-family: var(--kg-font-digits, inherit); font-weight: 700; overflow-wrap: anywhere; }
.vote-name { display: block; max-width: 100%; min-width: 0; font-size: 13px; font-weight: 600; line-height: 1.6; overflow-wrap: anywhere; }
.vote-podium-entry > .vote-name { min-height: 42px; }
.vote-count { display: block; max-width: 100%; min-width: 0; color: var(--act-text, #303442); line-height: 1.5; overflow-wrap: anywhere; }
.vote-count-value { font-family: var(--kg-font-digits, inherit); font-size: 20px; font-weight: 600; }
.vote-count-unit { margin-left: 3px; color: var(--act-secondary, #616783); font-size: 11px; font-weight: 400; }
.vote-podium-entry .vote-count { margin: 4px 0 12px; }
.activity-vote :deep(.activity-action.vote-button) { box-sizing: border-box; flex: none; width: 100%; max-width: 88px; min-height: 44px; margin: auto 0 0; padding: 9px 8px; border: 0; border-radius: 9px; background: var(--vote-fill); color: #fff; font-size: 14px; font-weight: 500; }
.activity-vote :deep(.activity-action.vote-button[disabled]:not([aria-busy='true'])) { background: var(--act-disabled, #e4e6ed); color: var(--act-secondary, #616783); }
.activity-vote :deep(.vote-button .activity-action__spinner) { width: 14px; height: 14px; }
.vote-podium--1 { grid-template-columns: minmax(0, 1fr); }
.vote-podium--1 .vote-podium-entry { grid-column: auto; grid-row: auto; display: grid; grid-template-columns: 64px minmax(0, 1fr) 64px; column-gap: 12px; padding: 16px 12px; text-align: left; }
.vote-podium--1 .vote-avatar--podium { grid-column: 1; grid-row: 1 / 3; width: 64px; height: 64px; margin: 0; }
.vote-podium--1 .vote-name { grid-column: 2; min-height: 0; font-size: 14px; }
.vote-podium--1 .vote-count { grid-column: 2; margin: 4px 0 0; }
.activity-vote .vote-podium--1 :deep(.vote-button) { grid-column: 3; grid-row: 1 / 3; margin: 0; }
.vote-podium--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.vote-podium--2 .vote-podium-entry:nth-child(n) { grid-column: auto; grid-row: auto; }
.vote-list { margin-top: 12px; }
.vote-row { display: grid; grid-template-columns: 52px minmax(0, 1fr) 64px; align-items: center; gap: 12px; min-height: 76px; padding: 12px 0; border-bottom: 1px solid var(--act-line, #f2f2f6); }
.vote-row-copy { min-width: 0; }
.vote-row .vote-count { margin-top: 3px; }
.activity-vote :deep(.activity-action.vote-row-action) { margin: 0; }
.vote-more { width: 100%; }
.activity-vote .vote-links { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0; margin-top: 20px; border-top: 1px solid var(--act-line, #f2f2f6); }
.activity-vote .vote-links :deep(.activity-action--text) { width: 100%; min-height: 48px; margin: 0; padding: 12px 8px; color: var(--vote-ink); font-size: 13px; }
.vote-links :deep(.activity-action + .activity-action) { border-left: 1px solid var(--act-line, #f2f2f6); border-radius: 0; }
.activity-vote :deep(.activity-action:focus-visible) { outline: 2px solid var(--vote-ink); outline-offset: 2px; }
@media (max-width: 350px) { .activity-vote { padding-inline: 12px; } .activity-vote .activity-section.vote-section { padding-inline: 12px; } .vote-podium--1 .vote-podium-entry { grid-template-columns: 52px minmax(0, 1fr) 60px; gap: 10px; padding-inline: 10px; } .vote-podium--1 .vote-avatar--podium { width: 52px; height: 52px; } }
.activity-vote{padding:0;background:transparent}.activity-vote .activity-section.vote-section{border-color:#e3ebf4;padding:24px}.vote-podium{max-width:720px;margin-inline:auto}.vote-list{max-width:780px;margin-inline:auto}@media(max-width:640px){.activity-vote .activity-section.vote-section{padding:18px 12px}}
</style>
